import React, { useState } from 'react';
import { X } from 'lucide-react';
import Header from '../components/Header';
import StatCard from '../components/StatCard';
import { useApp } from '../context/AppContext';
import { MOODS } from '../components/MoodSelector';
import { TRIGGERS } from '../components/TriggerSelector';

const MOOD_BAR_COLORS = {
  excellent: 'bg-brand-600',
  good: 'bg-brand-400',
  neutral: 'bg-gold-500',
  difficult: 'bg-bloom-400',
  critical: 'bg-bloom-600',
};

const StatisticsScreen = ({ navigate }) => {
  const { userData, dailyLogs } = useApp();
  const [frame, setFrame] = useState('month');

  const logs = Object.values(dailyLogs || {});
  const totalLogs = logs.length;

  const moodCounts = {};
  logs.forEach(log => {
    if (log.mood) moodCounts[log.mood] = (moodCounts[log.mood] || 0) + 1;
  });
  const moodData = MOODS.map(m => ({
    label: `${m.emoji} ${m.label}`,
    pct: totalLogs > 0 ? Math.round(((moodCounts[m.value] || 0) / totalLogs) * 100) : 0,
    color: MOOD_BAR_COLORS[m.value],
  }));

  const triggerCounts = {};
  logs.forEach(log => {
    (log.triggers || []).forEach(t => {
      triggerCounts[t] = (triggerCounts[t] || 0) + 1;
    });
  });
  const topTriggers = TRIGGERS
    .map(t => ({ label: t.label, count: triggerCounts[t.value] || 0 }))
    .filter(t => t.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const cleanLogs = logs.filter(l => l.isClean).length;
  const successRate = totalLogs > 0 ? Math.round((cleanLogs / totalLogs) * 100) : null;

  const topMood = MOODS
    .map(m => ({ ...m, count: moodCounts[m.value] || 0 }))
    .sort((a, b) => b.count - a.count)[0];
  const topMoodPct = topMood && topMood.count > 0 ? Math.round((topMood.count / totalLogs) * 100) : null;

  return (
    <div dir="rtl" className="min-h-screen bg-stone-50 pb-24">
      <Header
        title="סטטיסטיקה"
        subtitle="הבנה עמוקה של התקדמותך"
        rightAction={
          navigate && (
            <button onClick={() => navigate('home')} className="p-2 hover:bg-stone-100 rounded-lg">
              <X className="w-5 h-5 text-stone-500" />
            </button>
          )
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Main Stats */}
        <div className="bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl p-5 text-white shadow-lg">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold">{userData.streak}</p>
              <p className="text-brand-100 text-xs mt-1">רצף נוכחי</p>
            </div>
            <div className="border-x border-brand-400">
              <p className="text-3xl font-bold">{userData.totalDays}</p>
              <p className="text-brand-100 text-xs mt-1">ימים כוללים</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{userData.bestStreak}</p>
              <p className="text-brand-100 text-xs mt-1">הרצף הטוב ביותר</p>
            </div>
          </div>
        </div>

        {/* Frame selector */}
        <div className="bg-white rounded-xl p-3 shadow flex gap-2">
          {['month', 'quarter', 'year'].map(f => (
            <button
              key={f}
              onClick={() => setFrame(f)}
              className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-all ${
                frame === f ? 'bg-brand-500 text-white' : 'bg-stone-100 text-stone-700'
              }`}
            >
              {f === 'month' ? 'חודש' : f === 'quarter' ? 'רבעון' : 'שנה'}
            </button>
          ))}
        </div>

        {totalLogs === 0 ? (
          <div className="bg-white rounded-xl p-6 shadow text-center">
            <div className="text-3xl mb-2">📊</div>
            <p className="font-bold text-stone-800 mb-1">עדיין אין מספיק נתונים</p>
            <p className="text-sm text-stone-500">
              אחרי כמה בדיקות יומיות במסך הבית, יופיעו כאן פילוח רגשות, טריגרים ושיעור הצלחה אמיתיים.
            </p>
          </div>
        ) : (
          <>
            {/* Mood Chart */}
            <div className="bg-white rounded-xl p-4 shadow">
              <h3 className="font-bold text-stone-800 mb-4">😊 התפלגות רגשות</h3>
              <div className="space-y-3">
                {moodData.map(item => (
                  <div key={item.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold text-stone-700">{item.label}</span>
                      <span className="text-stone-500">{item.pct}%</span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-2.5 overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Triggers */}
            {topTriggers.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow">
                <h3 className="font-bold text-stone-800 mb-3">🎯 טריגרים נפוצים</h3>
                <div className="space-y-2">
                  {topTriggers.map(t => (
                    <div key={t.label} className="flex justify-between items-center bg-orange-50 rounded-lg p-3 border border-orange-100">
                      <span className="font-semibold text-stone-800 text-sm">{t.label}</span>
                      <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">{t.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Success Rate */}
            <div className="bg-white rounded-xl p-4 shadow text-center">
              <p className="font-bold text-stone-800 mb-3">📈 שיעור הצלחה</p>
              <p className="text-6xl font-bold text-brand-500 font-display">{successRate}<span className="text-3xl text-stone-500">%</span></p>
              <div className="mt-3 w-full bg-stone-200 rounded-full h-4 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-brand-400 to-brand-600" style={{ width: `${successRate}%` }} />
              </div>
              <p className="text-xs text-stone-600 mt-2">אתה עושה עבודה מצוינת!</p>
            </div>

            {/* Insights */}
            <div className="bg-brand-50 rounded-xl p-4 border border-brand-100">
              <h3 className="font-bold text-brand-900 mb-3">✨ תובנות</h3>
              <ul className="space-y-2 text-sm text-brand-800">
                <li>✓ יש לך רצף נוכחי של {userData.streak} ימים!</li>
                <li>✓ הרצף הטוב ביותר שלך: {userData.bestStreak} ימים</li>
                {topTriggers[0] && <li>✓ הטריגר הכי נפוץ שלך הוא "{topTriggers[0].label}"</li>}
                {topMoodPct !== null && <li>✓ הרגש הנפוץ ביותר שלך: "{topMood.label}" ({topMoodPct}%)</li>}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StatisticsScreen;
